import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gift, Percent, Tag, CheckCircle, Clock, Package } from "lucide-react";
import { KudozIcon } from "@/components/KudozIcon";
import { cn } from "@/lib/utils";
import KudozDisplay from "@/components/KudozDisplay";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useUserProfileOptional } from "@/hooks/useUserProfile";
import type { User, RedemptionItem, Redemption, Restaurant } from "@shared/schema";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Redeem() {
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<RedemptionItem | null>(null);
  const [redeemed, setRedeemed] = useState<Redemption | null>(null);
  const { toast } = useToast();
  
  // Get real Kudoz balance from user profile context (synced with external API)
  const userProfile = useUserProfileOptional();

  const { data: user, isLoading: userLoading } = useQuery<User>({
    queryKey: ['/api/user']
  });

  const { data: restaurants } = useQuery<Restaurant[]>({
    queryKey: ['/api/restaurants']
  });

  const { data: catalog, isLoading: catalogLoading } = useQuery<RedemptionItem[]>({
    queryKey: selectedRestaurantId 
      ? ['/api/redemptions/catalog', selectedRestaurantId]
      : ['/api/redemptions/catalog'],
    queryFn: async () => {
      const url = selectedRestaurantId 
        ? `/api/redemptions/catalog?restaurantId=${selectedRestaurantId}`
        : '/api/redemptions/catalog';
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch catalog');
      return response.json();
    }
  });

  const redeemMutation = useMutation({
    mutationFn: async (redemptionItemId: string) => {
      const response = await apiRequest('POST', '/api/redemptions/redeem', { redemptionItemId });
      return await response.json();
    },
    onSuccess: (data: { redemption: Redemption; message: string }) => {
      setRedeemed(data.redemption);
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      queryClient.invalidateQueries({ queryKey: ['/api/redemptions/catalog'] });
      queryClient.invalidateQueries({ queryKey: ['/api/redemptions/history'] });
      setSelectedItem(null);
    },
    onError: (error: any) => {
      toast({
        title: "Redemption Failed",
        description: error.message || "Unable to redeem reward. Please try again.",
        variant: "destructive"
      });
    }
  });

  const handleRedeemClick = (item: RedemptionItem) => {
    const currentBalance = userProfile?.kudozBalance ?? user?.kudozBalance ?? 0;
    if (!user || currentBalance < item.kudozCost) {
      toast({
        title: "Insufficient Kudoz",
        description: `You need ${item.kudozCost.toLocaleString()} Kudoz to redeem this reward.`,
        variant: "destructive"
      });
      return;
    }
    setSelectedItem(item);
  };

  const confirmRedeem = () => {
    if (!selectedItem) return;
    redeemMutation.mutate(selectedItem.id);
  };

  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'discount':
        return <Percent className="w-6 h-6" />;
      case 'free_item':
        return <Gift className="w-6 h-6" />;
      case 'gift_certificate':
        return <Tag className="w-6 h-6" />;
      default:
        return <Gift className="w-6 h-6" />;
    }
  };

  const getRewardBadgeColor = (type: string) => {
    switch (type) {
      case 'discount':
        return 'bg-blue-500/10 text-blue-600 dark:text-blue-400';
      case 'free_item':
        return 'bg-green-500/10 text-green-600 dark:text-green-400';
      case 'gift_certificate':
        return 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
      default:
        return '';
    }
  };

  const getRewardTypeLabel = (type: string) => {
    switch (type) {
      case 'discount':
        return 'Discount';
      case 'free_item':
        return 'Free Item';
      case 'gift_certificate':
        return 'Gift Certificate';
      default:
        return type;
    }
  };

  const getRewardValue = (item: RedemptionItem) => {
    if (item.type === 'discount' && item.value) {
      return `${parseFloat(item.value)}% Off`;
    } else if (item.type === 'gift_certificate' && item.value) {
      return `$${parseFloat(item.value)}`;
    } else if (item.type === 'free_item' && item.itemName) {
      return item.itemName;
    }
    return '';
  };

  const getRestaurantName = (restaurantId: string) => {
    const restaurant = restaurants?.find(r => r.id === restaurantId);
    return restaurant?.name || 'Unknown Restaurant';
  };

  if (userLoading || catalogLoading) {
    return (
      <div className="overflow-y-auto pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="h-32 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto pb-20 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Success Dialog */}
        <Dialog open={!!redeemed} onOpenChange={(open) => !open && setRedeemed(null)}>
          <DialogContent data-testid="dialog-success">
            <DialogHeader>
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-primary" />
                </div>
              </div>
              <DialogTitle className="text-center text-2xl">Redemption Successful!</DialogTitle>
              <DialogDescription className="text-center space-y-4 pt-4">
                <div className="bg-muted p-6 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Your Voucher Code</p>
                  <p className="text-3xl font-bold tracking-wider" data-testid="text-voucher-code">
                    {redeemed?.voucherCode}
                  </p>
                </div>
                <div className="text-sm space-y-2">
                  <p className="font-semibold">{(redeemed?.metadata as any)?.itemTitle}</p>
                  <p className="text-muted-foreground">
                    Show this code at {getRestaurantName(redeemed?.restaurantId || '')} to redeem your reward.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-muted-foreground pt-2">
                    <Clock className="w-4 h-4" />
                    <span className="text-xs">
                      Expires {redeemed?.expiresAt ? new Date(redeemed.expiresAt).toLocaleDateString() : 'never'}
                    </span>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button 
                className="w-full" 
                onClick={() => setRedeemed(null)}
                data-testid="button-close-success"
              >
                View My Redemptions
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog open={!!selectedItem && !redeemed} onOpenChange={(open) => !open && setSelectedItem(null)}>
          <DialogContent data-testid="dialog-confirm">
            <DialogHeader>
              <DialogTitle>Confirm Redemption</DialogTitle>
              <DialogDescription className="space-y-4 pt-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Reward</span>
                    <span className="font-semibold">{selectedItem?.title}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Restaurant</span>
                    <span className="font-semibold">
                      {selectedItem ? getRestaurantName(selectedItem.restaurantId) : ''}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Cost</span>
                    <span className="font-semibold text-kudoz">
                      {selectedItem?.kudozCost.toLocaleString()} Kudoz
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Value</span>
                    <span className="font-semibold text-primary">
                      {selectedItem ? getRewardValue(selectedItem) : ''}
                    </span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="text-muted-foreground">New Kudoz balance</span>
                    <span className="font-semibold">
                      {selectedItem ? ((userProfile?.kudozBalance ?? user?.kudozBalance ?? 0) - selectedItem.kudozCost).toLocaleString() : ''}
                    </span>
                  </div>
                </div>
                {selectedItem?.termsConditions && (
                  <div className="bg-muted p-3 rounded-md">
                    <p className="text-xs text-muted-foreground">
                      <strong>Terms:</strong> {selectedItem.termsConditions}
                    </p>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setSelectedItem(null)}
                disabled={redeemMutation.isPending}
                data-testid="button-cancel-redeem"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmRedeem}
                disabled={redeemMutation.isPending}
                data-testid="button-confirm-redeem"
              >
                {redeemMutation.isPending ? 'Redeeming...' : 'Confirm Redemption'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Header with Balance */}
        <div className="space-y-4">
          <h1 className="text-3xl font-bold">Redeem Kudoz</h1>
          
          {/* Kudoz Balance Card */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-kudoz/10 flex items-center justify-center">
                  <KudozIcon size="lg" className="w-7 h-7" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Your Kudoz Balance</p>
                  <div className="mt-1">
                    <KudozDisplay 
                      amount={userProfile?.kudozBalance ?? user?.kudozBalance ?? 0} 
                      size="lg"
                      showIcon={false}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter by Restaurant */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Filter by Restaurant</label>
          <Select 
            value={selectedRestaurantId || 'all'} 
            onValueChange={(value) => setSelectedRestaurantId(value === 'all' ? null : value)}
          >
            <SelectTrigger data-testid="select-restaurant-filter">
              <SelectValue placeholder="All Restaurants" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Restaurants</SelectItem>
              {restaurants?.map((restaurant) => (
                <SelectItem key={restaurant.id} value={restaurant.id}>
                  {restaurant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Available Rewards */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">
            Available Rewards {catalog && `(${catalog.length})`}
          </h2>
          
          {!catalog || catalog.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  No rewards available at this time.
                  {selectedRestaurantId && ' Try selecting a different restaurant.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {catalog.map((item) => {
                const canAfford = (userProfile?.kudozBalance ?? user?.kudozBalance ?? 0) >= item.kudozCost;
                const hasStock = item.stockLimit === null || (item.stockRemaining !== null && item.stockRemaining > 0);

                return (
                  <Card 
                    key={item.id}
                    className={cn(
                      "transition-all cursor-pointer",
                      canAfford && hasStock ? "hover-elevate active-elevate-2" : "opacity-60"
                    )}
                    onClick={() => canAfford && hasStock && handleRedeemClick(item)}
                    data-testid={`reward-item-${item.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start gap-4">
                        <div className={cn(
                          "w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0",
                          getRewardBadgeColor(item.type)
                        )}>
                          {getRewardIcon(item.type)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="font-semibold text-lg">{item.title}</h3>
                            <Badge className={cn("text-xs", getRewardBadgeColor(item.type))}>
                              {getRewardTypeLabel(item.type)}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-2">
                            {item.description}
                          </p>

                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                            <span>{getRestaurantName(item.restaurantId)}</span>
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <KudozIcon size="sm" />
                              <span className="font-semibold text-kudoz">
                                {item.kudozCost.toLocaleString()}
                              </span>
                            </div>
                            
                            <span className="text-muted-foreground">→</span>
                            
                            <span className="font-semibold text-primary">
                              {getRewardValue(item)}
                            </span>

                            {item.stockLimit !== null && (
                              <>
                                <span className="text-muted-foreground">•</span>
                                <div className="flex items-center gap-1">
                                  <Package className="w-4 h-4" />
                                  <span className="text-xs">
                                    {item.stockRemaining} left
                                  </span>
                                </div>
                              </>
                            )}
                          </div>

                          {!canAfford && (
                            <Badge variant="secondary" className="text-xs mt-2">
                              Need {(item.kudozCost - (userProfile?.kudozBalance ?? user?.kudozBalance ?? 0)).toLocaleString()} more Kudoz
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
